import { Component, ComponentType, ErrorInfo, ReactNode, lazy } from 'react'
import { Button } from '@/components/ui/button'
import { AlertTriangle, Home, RefreshCw } from 'lucide-react'
import { Sentry, isSentryInitialized } from '@/lib/sentry'
import { trackEvent } from '@/lib/analytics'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Error Boundary component that catches JavaScript errors in child components
 * and displays a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  /**
   * Checks if the error is related to failed chunk loading (common after deployments)
   */
  private isChunkError(error: Error | null): boolean {
    if (!error) return false
    return (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('error loading dynamically imported module')
    )
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo })

    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error)
    console.error('Component stack:', errorInfo.componentStack)

    // Send error to Sentry if initialized
    if (isSentryInitialized()) {
      Sentry.withScope((scope) => {
        scope.setExtra('componentStack', errorInfo.componentStack)
        Sentry.captureException(error)
      })
    }

    // Send analytics event
    trackEvent('ErrorBoundary', {
      error: error.name,
      message: error.message.substring(0, 200),
      isChunkError: this.isChunkError(error) ? 1 : 0,
    })
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
  }

  handleReload = (): void => {
    // Clear cache if it's a chunk error to ensure fresh assets
    if (this.isChunkError(this.state.error) && 'caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name)
        })
        window.location.reload()
      })
    } else {
      window.location.reload()
    }
  }

  handleGoHome = (): void => {
    window.location.href = '/'
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback
      }

      const isChunkLoadError = this.isChunkError(this.state.error)

      // Chunk loading error UI (deployment update)
      if (isChunkLoadError) {
        return (
          <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="max-w-md w-full text-center space-y-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10">
                <AlertTriangle className="w-8 h-8 text-accent" />
              </div>

              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-foreground">
                  Update Available
                </h1>
                <p className="text-muted-foreground">
                  A new version of the application has been deployed. Please reload the page to get the latest version.
                </p>
              </div>

              <Button onClick={this.handleReload} variant="default" size="lg">
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
            </div>
          </div>
        )
      }

      // Generic error UI
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>

            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-foreground">
                Something went wrong
              </h1>
              <p className="text-muted-foreground">
                An unexpected error occurred. Please try again or return to the home page.
              </p>
            </div>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <div className="text-left p-4 bg-muted rounded-lg overflow-auto max-h-48">
                <p className="text-sm font-mono text-destructive break-all">
                  {this.state.error.message}
                </p>
                {this.state.errorInfo && (
                  <pre className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Button onClick={this.handleReload} variant="default">
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
              <Button onClick={this.handleGoHome} variant="outline">
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

/**
 * Wrapper for lazy-loaded components with automatic reload on chunk errors.
 * Attempts one automatic page reload when chunk loading fails (common after deployments).
 * 
 * Usage: const AdminPage = lazyWithRetry(() => import('./AdminPage'))
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    )

    try {
      const component = await componentImport()
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false')
      return component
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Chunk loading error detected, try refreshing once
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true')
        console.log('Chunk loading failed, reloading page...')
        window.location.reload()
        // Return a never-resolving promise to prevent rendering
        return new Promise(() => {})
      }

      // Page has already been reloaded, throw the error to ErrorBoundary
      throw error
    }
  })
}

export default ErrorBoundary
